import React, { useCallback, useMemo } from "react";
import { getShouldShow } from "scmp-sdk/components/acquisition-popup-container/display/helpers";
import type {
  PopRenderPayload,
  PopupGetter,
  useRenderPopupOptions,
} from "scmp-sdk/components/acquisition-popup-container/display/types";
import { useSdkContext } from "scmp-sdk/components/contexts";

import { useResponsive } from "shared/hooks";
import { theme } from "shared/lib/styles";

import { WhyRegisterPopup } from ".";
import { getWhyRegisterActionKey } from "./actions";
import { SanitizedUrlKeys } from "./consts";

export const useWhyRegisterPopupGetter = (options: useRenderPopupOptions): PopupGetter => {
  const {
    acquisition: { popupState },
    currentRoute,
    handleRemoveRouteQuery,
    hasWhyRegisterPPTOHomepagePositionIntersected,
    userCountry,
    userTypeIds,
  } = useSdkContext();

  const getNextFromAction = useCallback((): PopRenderPayload | undefined => {
    const actionKey = getWhyRegisterActionKey(
      new URL(currentRoute, globalThis.location.origin).search,
    );
    if (!actionKey) return;
    void handleRemoveRouteQuery(SanitizedUrlKeys);
  }, [currentRoute, handleRemoveRouteQuery]);

  const isMobile = useResponsive(theme.breakpoints.only("mobile"), false);
  const getNextFromConfig = useCallback(
    (handleClose: () => void): PopRenderPayload | undefined => {
      const nextConfig = Object.values(popupState.whyRegister).find(
        whyRegister =>
          !whyRegister.positioningTrigger &&
          getShouldShow({
            config: whyRegister,
            userInfo: {
              isLoggedIn: options.isLoggedIn,
              userCountry: userCountry ?? "",
              userDevice: isMobile ? "mobile" : "desktop",
              userTypeIds,
            },
          }),
      );
      if (!nextConfig) return;

      const props = {
        modalContainerReference: options.modalContainerReference,
        onClose: handleClose,
        whyRegister: nextConfig,
      };

      return {
        delay: Number.parseInt(nextConfig.timeout ?? "0", 10),
        element: <WhyRegisterPopup {...props} />,
        localStorageKey: nextConfig.localStorageKey,
        updateLocalStorageOnShow: true,
      };
    },
    [
      isMobile,
      options.isLoggedIn,
      options.modalContainerReference,
      popupState.whyRegister,
      userCountry,
      userTypeIds,
    ],
  );

  const getNextFromScrollPosition = useCallback(
    (handleClose: () => void): PopRenderPayload | undefined => {
      const nextConfig = Object.values(popupState.whyRegister).find(
        whyRegister =>
          whyRegister.positioningTrigger &&
          getShouldShow({
            config: whyRegister,
            userInfo: {
              isLoggedIn: options.isLoggedIn,
              userCountry: userCountry ?? "",
              userDevice: isMobile ? "mobile" : "desktop",
              userTypeIds,
            },
          }),
      );
      if (!nextConfig || !hasWhyRegisterPPTOHomepagePositionIntersected) return;

      const props = {
        modalContainerReference: options.modalContainerReference,
        onClose: handleClose,
        whyRegister: nextConfig,
      };

      return {
        delay: Number.parseInt(nextConfig.timeout ?? "0", 10),
        element: <WhyRegisterPopup {...props} />,
        localStorageKey: nextConfig.localStorageKey,
        updateLocalStorageOnShow: true,
      };
    },
    [
      hasWhyRegisterPPTOHomepagePositionIntersected,
      isMobile,
      options.isLoggedIn,
      options.modalContainerReference,
      popupState.whyRegister,
      userCountry,
      userTypeIds,
    ],
  );

  const getters = useMemo(
    () => ({ getNextFromAction, getNextFromConfig, getNextFromScrollPosition }),
    [getNextFromAction, getNextFromConfig, getNextFromScrollPosition],
  );

  return getters;
};
